﻿import React, { useCallback } from 'react';
import moment from 'moment';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import { Translate } from '../../../../../../shared/LanguageService';
import MUISelect from '../../../../../../components/common/MUISelect';
import CustomDateTime from './CustomDateTime';
import {
    validateEndDateInAvailableDateRange,
    validateStartDateInAvailableDateRange
} from '../utils/orderDateUtils';
import {
    BrokerAgentFilterOptions,
    FreeTextFilterOptions,
    OrderPlacedAtFilterOptions,
    OrderTypeFilterOptions,
    TranslationProps
} from '../types';

type FilterOptionsProps = {
    orderTypeFilterOptions: OrderTypeFilterOptions;
    brokerAgentFilterOptions: BrokerAgentFilterOptions | null;
    freeTextFilterOptions: FreeTextFilterOptions;
    orderPlacedAtFilterOptions: OrderPlacedAtFilterOptions;
};

function FilterOptions({
    orderTypeFilterOptions,
    brokerAgentFilterOptions,
    freeTextFilterOptions,
    orderPlacedAtFilterOptions,
    labels,
    dynamicLabels,
}: FilterOptionsProps & TranslationProps) {
    const { availableOrderTypes, orderType, onChangeOrderType } = orderTypeFilterOptions;
    const { searchPhrase, onChangeFreeText } = freeTextFilterOptions;
    const {
        orderPlacedAtStart,
        orderPlacedAtEnd,
        onOrderPlacedAtStartChange,
        onOrderPlacedAtEndChange,
        earliestAvailableOrderDate
    } = orderPlacedAtFilterOptions;

    const validateStartDate = useCallback(
        (date): boolean => {
            const actualDate = date.toDate(); // Because "date" is a moment date object.
            return validateStartDateInAvailableDateRange(actualDate, earliestAvailableOrderDate);
        },
        [earliestAvailableOrderDate]
    );

    const validateEndDate = useCallback(
        (date): boolean => {
            const actualDate = date.toDate(); // Because "date" is a moment date object.
            return validateEndDateInAvailableDateRange(
                actualDate,
                orderPlacedAtStart,
                earliestAvailableOrderDate
            );
        },
        [orderPlacedAtStart, earliestAvailableOrderDate]
    );

    return (
        <div className="filterOptions__container">
            <h1 className="filterOptions__heading col-xs-12">{labels.title}</h1>

            <Grid container spacing={2}>
                <Grid item container spacing={2} lg={5} xs={12}>
                    <Grid item lg={4} sm={6} xs={12}>
                        <MUISelect
                            label={labels.orderTypeLabel}
                            autoFocus={true}
                            className="filterOptions__input"
                            value={orderType ? orderType : -1}
                            defaultOptionValue={-1}
                            defaultTranslateKey="orders/dropdown/order-type/default"
                            onChange={onChangeOrderType}
                        >
                            {availableOrderTypes.map((orderType) => {
                                return (
                                    <MenuItem value={orderType.value} key={orderType.value}>
                                        {dynamicLabels.orderTypeOption(orderType.translateKey)}
                                    </MenuItem>
                                );
                            })}
                        </MUISelect>
                    </Grid>

                    <Grid item lg={8} sm={6} xs={12}>
                        <TextField
                            name="filter"
                            label={labels.searchLabel}
                            value={searchPhrase}
                            onChange={onChangeFreeText}
                            className="filterOptions__input"
                            size="small"
                            fullWidth
                        />
                    </Grid>
                </Grid>

                <Grid item lg={4} md={8} sm={6} xs={12}>
                    {brokerAgentFilterOptions && brokerAgentFilterOptions.availableBrokerAgents.length > 1 ? (
                        <MUISelect
                            label={labels.brokerAgentLabel}
                            value={
                                brokerAgentFilterOptions.brokerAgent
                                    ? brokerAgentFilterOptions.brokerAgent
                                    : -1
                            }
                            defaultOptionValue={-1}
                            className="filterOptions__input"
                            defaultTranslateKey="orders/dropdown/broker-agent/default"
                            onChange={brokerAgentFilterOptions.onChangeBrokerAgent}
                        >
                            {brokerAgentFilterOptions.availableBrokerAgents.map((brokerAgent) => {
                                return (
                                    <MenuItem value={brokerAgent.id} key={brokerAgent.id}>
                                        {brokerAgent.name}
                                    </MenuItem>
                                );
                            })}
                        </MUISelect>
                    ) : null}
                </Grid>

                <Grid item container spacing={2} lg={3} md={4} sm={6} xs={12}>
                    <Grid item xs={6}>
                        <CustomDateTime
                            label={labels.orderPlacedAtStartLabel}
                            locale={moment.locale()}
                            inputProps={{ style: { height: '38px', width: '100%' } }}
                            onChange={(newDate) => {
                                onOrderPlacedAtStartChange(
                                    new Date(newDate)
                                );
                            }}
                            timeFormat={false}
                            value={orderPlacedAtStart}
                            isValidDate={validateStartDate}
                            closeOnSelect={true}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <CustomDateTime
                            label={labels.orderPlacedAtEndLabel}
                            locale={moment.locale()}
                            inputProps={{ style: { height: '38px', width: '100%' } }}
                            onChange={(newDate) => {
                                onOrderPlacedAtEndChange(
                                    new Date(newDate)
                                );
                            }}
                            timeFormat={false}
                            value={orderPlacedAtEnd}
                            isValidDate={validateEndDate}
                            closeOnSelect={true}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </div>
    );
}

function TranslatedFilterOptions(props: FilterOptionsProps) {
    const labels = {
        title: Translate('orders/title/orders'),
        orderTypeLabel: Translate('orders/dropdown/title/order-type'),
        defaultSelectedOrderType: Translate('orders/dropdown/order-type/default'),
        searchLabel: Translate('orders/dropdown/title/filter'),
        brokerAgentLabel: Translate('orders/dropdown/title/broker-agent'),
        orderPlacedAtStartLabel: Translate('orders/list/broker-agent/filter/start-date'),
        orderPlacedAtEndLabel: Translate('orders/list/broker-agent/filter/end-date'),
        showArchivedLabel: Translate('orders/list/broker-agent/filter/show-archived')
    };

    const dynamicLabels = {
        orderTypeOption: (translationKey: string): string => Translate(translationKey)
    };

    return <FilterOptions {...props} labels={labels} dynamicLabels={dynamicLabels} />;
}

export default TranslatedFilterOptions;
